export type IntercomOpenOutcome = 'opened' | 'script_failed' | 'timed_out';

/** One attempt to open the Intercom messenger, started by a tap. */
export interface IntercomOpenAttempt {
  /** Entry point that started the attempt; the `from` of the tap event. */
  source: string;
  startedAt: number;
  /** Whether the identity token arrived; `null` until the request answers. */
  tokenObtained: boolean | null;
}

export interface IntercomOpenReport extends IntercomOpenAttempt {
  outcome: IntercomOpenOutcome;
  elapsedMs: number;
}

/**
 * Tracks the pending attempt to open the messenger, from the tap to the
 * moment it is known to have succeeded or failed, and reports each attempt
 * exactly once.
 *
 * Free of DOM, store and SDK imports so the timing and dedupe rules can be
 * unit-tested: a widget script that fails ends the attempt and silences the
 * timeout, the timeout fires only while the attempt is still pending, a new
 * tap replaces the pending attempt, and `elapsedMs` always counts from the
 * tap. `useIntercom` wires the outcomes to telemetry and analytics.
 */
export const createIntercomOpenTracker = (
  timeoutMs: number,
  report: (report: IntercomOpenReport) => void,
  now: () => number = Date.now,
) => {
  let attempt: IntercomOpenAttempt | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const finish = (outcome: IntercomOpenOutcome) => {
    if (!attempt) return;
    const subject = attempt;
    clearTimeout(timer);
    timer = undefined;
    attempt = null;
    report({ ...subject, outcome, elapsedMs: now() - subject.startedAt });
  };

  return {
    start(source: string) {
      clearTimeout(timer);
      const current: IntercomOpenAttempt = {
        source,
        startedAt: now(),
        tokenObtained: null,
      };
      attempt = current;
      timer = setTimeout(() => {
        if (attempt === current) finish('timed_out');
      }, timeoutMs);
    },
    tokenResolved(obtained: boolean) {
      if (attempt) attempt.tokenObtained = obtained;
    },
    scriptFailed() {
      finish('script_failed');
    },
    opened() {
      finish('opened');
    },
    cancel() {
      clearTimeout(timer);
      timer = undefined;
      attempt = null;
    },
  };
};
